import { useEffect, useState } from 'react';
import { doc, setDoc, getDoc, getFirestore, onSnapshot } from 'firebase/firestore';
import { FaTrash, FaInfoCircle } from 'react-icons/fa';
import { Tooltip } from "react-tooltip";
import { useDebounce } from 'use-debounce';
import { useUserId } from "hooks/user/useUserId";
import {
  ProjectKeysContainer,
  TaskTitle,
  Card,
  DeleteButton,
  OpenAIKeyInputDiv,
  OpenAIKeyInput,
} from './ProjectKeysStyles';

const KeyDiv = ({ projectKeysRef }) => {
  const FREE_VERSION = 'Free API Key';

  // Init the key to be null
  const [openaiKey, setOpenaiKey] = useState(null);

  // Initialize the debounced OpenAI key
  const [debouncedOpenaiKey] = useDebounce(openaiKey, 250);

  // Load the OpenAI key from Firestore when the component mounts 
  // and projectKeysRef is available
  useEffect(() => {
    if (!projectKeysRef) return;

    // Load the stored OpenAI key from projectKeys/{projectId}
    const loadOpenaiKey = async () => {
      try {
        const projectKeysDoc = await getDoc(projectKeysRef);
        if (projectKeysDoc.exists()) {
          // A project key document exists for this project
          const projectKeysdata = projectKeysDoc.data();

          // Set the OpenAI key to the stored key if it exists
          // Otherwise, set it to the free version
          const storedOpenaiKey = projectKeysdata.openaiApiKey || FREE_VERSION;
          setOpenaiKey(storedOpenaiKey);
        } else {
          // Initialize the OpenAI key to the free version
          setOpenaiKey(FREE_VERSION);
        }
      } catch (error) {
        console.error('Error loading OpenAI key: ', error);
      }
    };
    loadOpenaiKey();
  }, [projectKeysRef]);

  // Listen to the debounced OpenAI key (which updates when the user stops typing)
  // Update the OpenAI key in Firestore to the new value (as long as the stored key has loaded)
  useEffect(() => {
    if (!projectKeysRef || openaiKey === null) return;

    const updateOpenaiKey = async () => {
      try {
        await setDoc(projectKeysRef, { openaiApiKey: debouncedOpenaiKey }, { merge: true });
      } catch (error) {
        console.error('Error updating openai key: ', error);
      }
    };
    updateOpenaiKey();
  }, [debouncedOpenaiKey]);

  // Reset the OpenAI key which will trigger the update in Firestore
  const deleteOpenaiKey = () => {
    setOpenaiKey(FREE_VERSION);
  };

  return (
    <Card>
      <TaskTitle>Your OpenAI API Key</TaskTitle>
      <OpenAIKeyInputDiv>
        <OpenAIKeyInput
          type="text"
          value={openaiKey}
          onChange={e => setOpenaiKey(e.target.value)}
          placeholder="Free API Key"
        />
        <DeleteButton onClick={deleteOpenaiKey} title="Remove API Key">
          <FaTrash />
        </DeleteButton>
      </OpenAIKeyInputDiv>

      <div className='mt-2'>
        Need an API key? Visit the{' '}
        <a
          href="https://platform.openai.com/api-keys"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          OpenAI API Key portal
        </a>{' '}
        to generate one.
      </div>
    </Card>
  );
};

// Tooltip component for usage explanation
const StyledTooltip = ({ id, text }) => {
  return (
    <>
      <span
        data-tooltip-id={id}
        style={{
          marginLeft: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer',
          color: '#4A5568', // subtle gray, like Tailwind gray-700
          fontSize: '14px'
        }}
      >
        <FaInfoCircle />
      </span>
      <Tooltip
        id={id}
        style={{
          backgroundColor: '#333', // dark gray background
          color: '#fff',           // white text
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          maxWidth: '300px',
          zIndex: 9999,             // ensure it’s on top
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: "300px" }}>
          {text}
        </div>
      </Tooltip>
    </>);
};

const UsageDiv = () => {
  // The default usage is just no usage
  const DEFAULT_USAGE = {
    requestCount: 0,
    tokenCount: 0,
  };

  const userId = useUserId();
  const [unauthenticatedUsage, setUnauthenticatedUsage] = useState(DEFAULT_USAGE);
  const [authenticatedUsage, setAuthenticatedUsage] = useState(DEFAULT_USAGE);

  const usageExplanation = `We provide 1 million free tokens for "In IDE" (while you're coding) and for "Sharing" (when others run your shared project). 
This shows how many of those free tokens you've used. Once you hit the limit, just add your own OpenAI API key to keep going!`;

  useEffect(() => {
    if (!userId) return;

    try {
      // Get a reference the the usage document for the user
      const db = getFirestore();
      const usageRef = doc(db, 'notopenai', userId);

      // Listen to the usage document for changes
      const unsubscribe = onSnapshot(usageRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setUnauthenticatedUsage(data.unauthenticatedUsage ?? DEFAULT_USAGE);
          setAuthenticatedUsage(data.authenticatedUsage ?? DEFAULT_USAGE);
        }
      }, (error) => {
        console.error('Error listening to usage document:', error);
      });

      // Cleanup the listener when the component unmounts
      return () => unsubscribe();
    } catch (error) {
      console.error('Error fetching usage data:', error);
    }
  }, [userId]);

  return (
    <Card>
      <TaskTitle>Current Usage (Free API Key)
        <StyledTooltip id="usageExplanation" text={usageExplanation} />
      </TaskTitle>
      <div>
        <strong>In IDE</strong>
        <ul>
          <li>Requests: {authenticatedUsage.requestCount}</li>
          <li>Tokens: {authenticatedUsage.tokenCount} / 1,000,000</li>
        </ul>
      </div>
      <div style={{ marginTop: '1rem' }}>
        <strong>Sharing</strong>
        <ul>
          <li>Requests: {unauthenticatedUsage.requestCount}</li>
          <li>Tokens: {unauthenticatedUsage.tokenCount} / 1,000,000</li>
        </ul>
      </div>
    </Card>
  );
};

const ResourceDiv = () => {
  return (
    <Card>
      <TaskTitle>➕ Connect a New OpenAI API Key</TaskTitle>
      <p>
        Need an API key? Visit the{' '}
        <a
          href="https://platform.openai.com/api-keys"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          OpenAI API Key portal
        </a>{' '}
        to generate one.
      </p>
    </Card>
  );
};

const ApiKeyInfoDiv = () => {
  return (
    <Card>
      <TaskTitle>About API Keys</TaskTitle>
      <p>
        An <strong>API key</strong> is like a password that your project uses to make requests to OpenAI.
        We automatically provide you with a key and a limited amount of usage (Free API Key).
        If you'd like to use your own OpenAI API key for this project, add it here.
      </p>
    </Card>
  );
};

export const ProOpenaiSplash = ({ projectKeysRef }) => {
  return (
    <ProjectKeysContainer>
      <TaskTitle>Manage Your OpenAI API Connection</TaskTitle>
      <ApiKeyInfoDiv />
      <UsageDiv />
      <KeyDiv projectKeysRef={projectKeysRef} />
      
      {/* <ResourceDiv /> */}
    </ProjectKeysContainer>
  );
};
